import { Autocomplete as MuiAutocomplete, IconButton, TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { LocationModel } from '@/types/location';
import ClearIcon from '@mui/icons-material/Clear';
import React, { useMemo, useState, useEffect } from 'react';
import styles from './LocationAutocomplete.module.scss';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { ActivityKindEnum, PropertyKindEnum, TransactionTypeEnum } from '@/types';

import { getDepthLabel } from './locationFormatting';

export { getDepthLabel } from './locationFormatting';

interface LocationAutocompleteProps {
  ariaLabel?: string;
  onSelect?: (value: LocationModel) => void;
  locations: LocationModel[];
  selectedLocation?: LocationModel | null;
  inputAddressValue: string;
  setInputAddressValue: (value: string) => void;
  onInputChange?: (value: string) => void;
  handleClear?: () => void;
  activityKind?: ActivityKindEnum;
  propertyKind?: PropertyKindEnum;
  transactionType?: TransactionTypeEnum;
  isPageChanged?: boolean;
  isAddNewLocationVisible?: boolean;
  isDisable?: boolean;
  isLoading?: boolean;
  clearOnDependenciesChange?: boolean;
  autoSelectFirstOnEnter?: boolean;
}

export const LocationAutocomplete = ({
  ariaLabel,
  locations = [],
  onSelect,
  selectedLocation,
  inputAddressValue,
  setInputAddressValue,
  onInputChange,
  handleClear,
  activityKind,
  propertyKind,
  transactionType,
  isPageChanged,
  isAddNewLocationVisible,
  isDisable,
  isLoading = false,
  clearOnDependenciesChange = true,
  autoSelectFirstOnEnter = true,
}: LocationAutocompleteProps) => {
  const formContext = useFormContext();
  const clearErrors = formContext?.clearErrors;
  const errors = formContext?.formState.errors || {};

  const [value, setValue] = useState<LocationModel | undefined>(undefined);
  const handleSelectValue = (value: LocationModel) => {
    setValue(value);
    setInputAddressValue(value[value.kind] ?? '');
    onSelect?.(value);
    if (clearErrors) {
      clearErrors(['addressEn']);
    }
  };

  const onClear = () => {
    setInputAddressValue('');
    setValue(undefined);
    handleClear?.();
  };

  useEffect(() => {
    if (!clearOnDependenciesChange) return;

    if (activityKind || propertyKind || transactionType || isAddNewLocationVisible) {
      if (!isPageChanged) {
        onClear();
      }
    }
  }, [activityKind, propertyKind, transactionType, isAddNewLocationVisible]);

  useEffect(() => {
    if (!inputAddressValue) setValue(undefined);
  }, [inputAddressValue]);

  useEffect(() => {
    if (selectedLocation !== undefined) setValue(selectedLocation ?? undefined);
  }, [selectedLocation]);

  const handlePressEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (value) {
      onSelect?.(value);
    } else if (autoSelectFirstOnEnter && locations[0] && inputAddressValue) {
      setValue(locations[0]);
      setInputAddressValue(locations[0]?.[locations[0].kind] ?? '');
      onSelect?.(locations[0]);
    }
  };

  const options = useMemo(
    () => (inputAddressValue ? locations : []),
    [inputAddressValue, locations]
  );

  return (
    <MuiAutocomplete
      onChange={(e, value) => value && handleSelectValue(value)}
      disabled={isDisable}
      loading={isLoading}
      fullWidth={true}
      // Keep the empty value controlled; MUI's disableClearable type omits its supported null state.
      value={(value ?? null) as LocationModel | undefined}
      inputValue={inputAddressValue}
      disableClearable={true}
      autoComplete={false}
      onKeyDown={handlePressEnter}
      noOptionsText={''}
      autoSelect={false}
      filterSelectedOptions={false}
      filterOptions={(option) => option}
      classes={{ inputRoot: styles.inputRoot, root: styles.root, noOptions: styles.noOptions }}
      options={options}
      getOptionLabel={(option) => option[option.kind] ?? ''}
      renderOption={(props, option, { inputValue }) => {
        const matches = match(option[option.kind] ?? '', inputValue, { insideWords: true });
        const parts = parse(option[option.kind] ?? '', matches);
        const depthLabel = getDepthLabel(option);
        const kindLabel =
          option.kind === 'city' ? `(City)` : option.kind === 'region' ? `(Region)` : null;

        return (
          <li key={option.id} {...props}>
            <div>
              {parts?.map((part, index) => (
                <span
                  key={`${option.id}-${index}`}
                  style={{
                    fontWeight: part.highlight ? 600 : 400,
                  }}
                >
                  {part.text}
                </span>
              ))}
              <span className={styles.gray}>
                {kindLabel ? kindLabel : depthLabel && ' ' + `(${depthLabel})`}
              </span>
            </div>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          }}
          name='addressEn'
          error={!!errors['addressEn']}
          helperText={errors['addressEn'] ? errors['addressEn']?.message?.toString() : ''}
          fullWidth={true}
          onChange={(event) => {
            const val = event.target.value;
            if (val) {
              setValue(undefined);
              setInputAddressValue(val);
              onInputChange?.(val);
            } else {
              onClear();
            }
          }}
          placeholder='Enter the address'
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <IconButton
                aria-label='Clear location'
                sx={{
                  visibility: !isDisable && inputAddressValue ? 'visible' : 'hidden',
                }}
                onClick={onClear}
              >
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      )}
    />
  );
};
